import { useCallback, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MemeCanvas } from '../components/meme/MemeCanvas';
import { MemeNFTSearch } from '../components/meme/MemeNFTSearch';
import { CollapsibleSection, Toggle, Button, ImagePreviewModal, FontDropdown } from '../components/common';
import { ThemeToggle } from '../components/common/ThemeToggle';
import { useMemeStore, useUIStore, useThemeStore } from '../stores';
import { MEME_FONTS, MEME_PRESETS } from '../constants';
import { downloadImage, drawWrappedText, isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../utils';
import { playClickSound } from '../utils/gameSound';

// Icon components
function BackArrowIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function NubFaceIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-3-9c.83 0 1.5-.67 1.5-1.5S9.83 8 9 8s-1.5.67-1.5 1.5S8.17 11 9 11zm6 0c.83 0 1.5-.67 1.5-1.5S15.83 8 15 8s-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm-3 5.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
    </svg>
  );
}

function TextIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5 4v3h5.5v12h3V7H19V4H5z"/>
    </svg>
  );
}

function LayersIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function DownloadIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function TrashIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}


function BackgroundIcon({ className = '', active = false }: { className?: string; active?: boolean }) {
  return (
    <svg className={className} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 15l4-4 3 3 4-4 7 7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function EditorPage() {
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const { openModal } = useUIStore();
  const bgInputRef = useRef<HTMLInputElement>(null);
  const textPropertiesRef = useRef<HTMLDivElement>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [mobileTextDraft, setMobileTextDraft] = useState('');
  const [keyboardInset, setKeyboardInset] = useState(0);

  // Preview modal state for Telegram fallback
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    document.documentElement.setAttribute('dir', 'ltr');
    document.body.setAttribute('dir', 'ltr');
  }, [theme]);

  const {
    canvasWidth,
    canvasHeight,
    backgroundImage,
    backgroundImageScale,
    backgroundImageOffset,
    backgroundColor,
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    selectElement,
    removeNFTElement,
    duplicateNFTElement,
    bringToFront,
    sendToBack,
    updateNFTElementScale,
    updateNFTElementRotation,
    updateNFTElementLayers,
    updateNFTElementStickerMode,
    setExclusiveNFTBackground,
    removeTextElement,
    updateTextElement,
    addTextElement,
    setBackgroundImage,
    setBackgroundImageScale,
    setBackgroundImageOffset,
    setBackgroundColor,
    activePreset,
    setPreset,
  } = useMemeStore();

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;

      const img = new Image();
      img.onload = () => {
        setBackgroundImage(dataUrl, { width: img.width, height: img.height });
        nftElements.forEach((el) => {
          updateNFTElementLayers(el.id, el.selectedLayers, false, el.useFrameLayer);
        });
      };
      img.onerror = () => {
        setBackgroundImage(dataUrl);
        nftElements.forEach((el) => {
          updateNFTElementLayers(el.id, el.selectedLayers, false, el.useFrameLayer);
        });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  const handleClearBackground = () => {
    setBackgroundImage(null);
  };

  const handleResetBackgroundPosition = () => {
    setBackgroundImageOffset({ x: 0, y: 0 });
    if (backgroundImage) {
      const img = new Image();
      img.onload = () => {
        const imgAspect = img.width / img.height;
        const canvasAspect = canvasWidth / canvasHeight;
        let scale: number;
        if (imgAspect > canvasAspect) {
          scale = canvasHeight / img.height;
        } else {
          scale = canvasWidth / img.width;
        }
        setBackgroundImageScale(scale);
      };
      img.src = backgroundImage;
    }
  };

  const handleAddNFT = () => {
    playClickSound();
    openModal('memeSearch');
  };

  const handleAddText = () => {
    playClickSound();
    addTextElement('');
  };

  const handlePresetClick = (presetId: string) => {
    playClickSound();
    const preset = MEME_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setPreset(preset);
    }
  };

  const selectedNFT = selectedElementType === 'nft'
    ? nftElements.find((e) => e.id === selectedElementId)
    : null;

  const selectedText = selectedElementType === 'text'
    ? textElements.find((e) => e.id === selectedElementId)
    : null;

  // Auto-scroll to text properties when a text element is selected
  useEffect(() => {
    if (selectedText && textPropertiesRef.current) {
      setTimeout(() => {
        textPropertiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedText?.id]);

  useEffect(() => {
    const hasCharacterBackground = nftElements.some((element) => element.useBackgroundLayer);
    const targetColor = !backgroundImage && !hasCharacterBackground ? '#ffffff' : '#1a1a2e';
    if (backgroundColor !== targetColor) {
      setBackgroundColor(targetColor);
    }
  }, [backgroundColor, backgroundImage, nftElements, setBackgroundColor]);

  const enforceLtrInput = useCallback((input: HTMLInputElement) => {
    input.setAttribute('dir', 'ltr');
    input.style.setProperty('direction', 'ltr', 'important');
    input.style.setProperty('unicode-bidi', 'normal', 'important');
    input.style.setProperty('text-align', 'left', 'important');
    input.style.setProperty('writing-mode', 'horizontal-tb', 'important');
  }, []);

  const handleContentInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    if (!selectedText) return;
    const input = e.currentTarget;
    enforceLtrInput(input);
    updateTextElement(selectedText.id, { text: input.value });
  }, [selectedText, updateTextElement, enforceLtrInput]);

  const handleOpenMobileTextModal = useCallback(() => {
    if (!selectedText) return;
    setMobileTextDraft(selectedText.text);
    setIsTextModalOpen(true);
  }, [selectedText]);

  const handleSaveMobileText = useCallback(() => {
    if (!selectedText) return;
    updateTextElement(selectedText.id, { text: mobileTextDraft });
    setIsTextModalOpen(false);
  }, [selectedText, mobileTextDraft, updateTextElement]);

  useEffect(() => {
    const onResize = () => setIsNarrowViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const updateInset = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardInset(inset);
    };
    updateInset();
    viewport.addEventListener('resize', updateInset);
    viewport.addEventListener('scroll', updateInset);
    return () => {
      viewport.removeEventListener('resize', updateInset);
      viewport.removeEventListener('scroll', updateInset);
    };
  }, []);

  const handleNFTFrameToggle = (checked: boolean) => {
    if (selectedNFT) {
      updateNFTElementLayers(
        selectedNFT.id,
        selectedNFT.selectedLayers,
        selectedNFT.useBackgroundLayer,
        checked
      );
    }
  };

  const hasBackgroundLayers = selectedNFT?.nftData.layerAssets.some(isBackgroundLayer);

  const hasFrameLayers = selectedNFT?.nftData.layerAssets.some(isFrameLayer);

  const aspectPresets = [
    { id: 'square', label: 'square', width: 1, height: 1 },
    { id: 'portrait', label: 'story', width: 9, height: 16 },
    { id: 'landscape', label: 'banner', width: 16, height: 9 },
  ];

  // Export handler
  const handleExport = useCallback(async () => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasWidth;
    exportCanvas.height = canvasHeight;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }

    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    if (backgroundImage) {
      try {
        const bgImg = await loadImage(backgroundImage);
        const scaledWidth = bgImg.width * backgroundImageScale;
        const scaledHeight = bgImg.height * backgroundImageScale;
        const drawX = (canvasWidth - scaledWidth) / 2 + backgroundImageOffset.x;
        const drawY = (canvasHeight - scaledHeight) / 2 + backgroundImageOffset.y;

        ctx.drawImage(bgImg, drawX, drawY, scaledWidth, scaledHeight);
      } catch (err) {
        console.error('Failed to load background image:', err);
      }
    }

    const sortedNFTElements = [...nftElements].sort((a, b) => a.zIndex - b.zIndex);

    // In sticker mode the canvas preview shows ONLY the sticker element (no text,
    // no other characters) — the export must match the preview.
    const stickerModeElement = nftElements.find((el) => el.stickerMode);

    const allElements: Array<{ type: 'nft' | 'text'; element: typeof nftElements[0] | typeof textElements[0] }> = stickerModeElement
      ? [{ type: 'nft' as const, element: stickerModeElement }]
      : [
          ...nftElements.map((e) => ({ type: 'nft' as const, element: e })),
          ...textElements.map((e) => ({ type: 'text' as const, element: e })),
        ].sort((a, b) => a.element.zIndex - b.element.zIndex);

    const drawBackgroundLayer = (layerImg: HTMLImageElement) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;
      let drawY: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        drawY = (canvasHeight - drawHeight) / 2;
      }

      ctx.drawImage(layerImg, drawX, drawY, drawWidth, drawHeight);
    };

    const shouldRenderNFTBackgrounds = !backgroundImage;

    // PASS 1: Draw ALL background layers first (in z-order)
    // Skipped entirely in sticker mode (mirrors MemeCanvas)
    if (shouldRenderNFTBackgrounds && !stickerModeElement) {
      for (const nftEl of sortedNFTElements) {
        if (!nftEl.useBackgroundLayer) continue;
        if (nftEl.stickerMode) continue;

        const backgroundLayers = nftEl.nftData.layerAssets.filter(isBackgroundLayer);

        for (const layer of backgroundLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            drawBackgroundLayer(layerImg);
          } catch (err) {
            console.error('Failed to load background layer for export:', layer.id, err);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers and text elements (in z-order)
    for (const { type, element } of allElements) {
      ctx.save();

      if (type === 'nft') {
        const nftEl = element as typeof nftElements[0];
        const layers = nftEl.nftData.layerAssets;

        ctx.translate(nftEl.position.x, nftEl.position.y);
        ctx.rotate((nftEl.rotation * Math.PI) / 180);

        if (layers.length > 0) {
          const characterLayers = layers.filter((layer) => {
            if (nftEl.selectedLayers.length > 0) {
              return nftEl.selectedLayers.includes(layer.id);
            }

            // Background/frame handled in pass 1 / pass 3
            return isCharacterLayer(layer);
          });

          let refWidth = 512;
          let refHeight = 512;
          try {
            const refImg = await loadImage(nftEl.nftData.imageUrl);
            refWidth = refImg.width;
            refHeight = refImg.height;
          } catch {
            // Use default dimensions if composite image fails to load
          }

          for (const layer of characterLayers) {
            try {
              const layerImg = await loadImage(layer.uri);
              const scaledWidth = refWidth * nftEl.scale;
              const scaledHeight = refHeight * nftEl.scale;
              ctx.drawImage(
                layerImg,
                -scaledWidth / 2,
                -scaledHeight / 2,
                scaledWidth,
                scaledHeight
              );
            } catch (err) {
              console.error('Failed to load layer for export:', layer.id, err);
            }
          }
        } else {
          try {
            const img = await loadImage(nftEl.nftData.imageUrl);
            const scaledWidth = img.width * nftEl.scale;
            const scaledHeight = img.height * nftEl.scale;
            ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          } catch (err) {
            console.error('Failed to load NFT image for export:', err);
          }
        }
      } else {
        const textEl = element as typeof textElements[0];
        ctx.translate(textEl.position.x, textEl.position.y);
        ctx.rotate((textEl.rotation * Math.PI) / 180);

        ctx.direction = 'ltr';
        ctx.font = `${textEl.fontSize}px ${textEl.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        drawWrappedText(ctx, textEl.text, textEl.fontSize, canvasWidth * 0.9, {
          color: textEl.color,
          strokeColor: textEl.strokeColor,
          strokeWidth: textEl.strokeWidth,
        });
      }

      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last (in z-order)
    // Skipped entirely in sticker mode (mirrors MemeCanvas)
    if (!stickerModeElement)
    for (const nftEl of sortedNFTElements) {
      if (!nftEl.useFrameLayer) continue;
      if (nftEl.stickerMode) continue;

      const frameLayers = nftEl.nftData.layerAssets.filter(isFrameLayer);

      for (const layer of frameLayers) {
        try {
          const layerImg = await loadImage(layer.uri);
          drawBackgroundLayer(layerImg);
        } catch (err) {
          console.error('Failed to load frame layer for export:', layer.id, err);
        }
      }
    }

    exportCanvas.toBlob(async (blob) => {
      if (blob) {
        const filename = `nft_meme_${canvasWidth}x${canvasHeight}.png`;

        await downloadImage(blob, filename, {
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
        });
      }
    }, 'image/png');
  }, [canvasWidth, canvasHeight, backgroundImage, backgroundImageScale, backgroundImageOffset, backgroundColor, nftElements, textElements]);

  return (
    <div dir="ltr" className="min-h-screen bg-neutral-950 flex flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-neutral-800/50 border border-neutral-700/50 hover:bg-neutral-700/50 transition-colors"
          aria-label="Go back"
        >
          <BackArrowIcon className="w-5 h-5 text-neutral-300" />
        </button>

        {/* Theme Toggle */}
        <ThemeToggle />
      </div>

      {/* Canvas Area */}
      <div className="flex-1 flex items-center justify-center px-4 py-2">
        <div className="w-full max-w-md">
          <MemeCanvas maxViewportWidth={400} maxViewportHeight={400} />
        </div>
      </div>

      {/* Bottom Toolbar */}
      <div className="flex items-center justify-center gap-3 px-4 py-4">
        {/* NFT Button */}
        <button
          onClick={handleAddNFT}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-white text-neutral-800 shadow-lg hover:bg-neutral-100 transition-all active:scale-95"
          aria-label="Add NFT"
          title="Add NFT"
        >
          <NubFaceIcon className="w-7 h-7" />
        </button>

        {/* Text Button */}
        <button
          onClick={handleAddText}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-white text-neutral-800 shadow-lg hover:bg-neutral-100 transition-all active:scale-95"
          aria-label="Add Text"
          title="Add Text"
        >
          <TextIcon className="w-7 h-7" />
        </button>

        {/* Layers/Settings Button */}
        <button
          onClick={() => {
            playClickSound();
            const settingsSection = document.getElementById('canvas-settings-section');
            if (settingsSection) {
              settingsSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-white text-neutral-800 shadow-lg hover:bg-neutral-100 transition-all active:scale-95"
          aria-label="Canvas Settings"
          title="Canvas Settings"
        >
          <LayersIcon className="w-7 h-7" />
        </button>

        {/* Download Button */}
        <button
          onClick={handleExport}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-white text-neutral-800 shadow-lg hover:bg-neutral-100 transition-all active:scale-95"
          aria-label="Download"
          title="Download PNG"
        >
          <DownloadIcon className="w-7 h-7" />
        </button>
      </div>

      {/* Canvas Settings Section */}
      <div id="canvas-settings-section" className="bg-neutral-900 border-t border-neutral-800 rounded-t-3xl overflow-hidden">
        <div className="px-4 max-h-[50vh] overflow-y-auto">
          <CollapsibleSection title="Canvas Settings" defaultOpen={false}>
            <div className="space-y-4">
              {/* Aspect Ratio Preview Buttons */}
              <div className="grid grid-cols-3 gap-2">
                {aspectPresets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handlePresetClick(preset.id)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                      activePreset?.id === preset.id
                        ? 'border-game-blue bg-game-blue/10 ring-1 ring-game-blue'
                        : 'border-neutral-700 hover:bg-neutral-800/50'
                    }`}
                  >
                    <div
                      className={`rounded-sm transition-colors ${
                        activePreset?.id === preset.id
                          ? 'bg-game-blue'
                          : 'bg-neutral-600'
                      } ${
                        preset.id === 'square' ? 'w-6 h-6' :
                        preset.id === 'portrait' ? 'w-4 h-6' :
                        'w-8 h-4'
                      }`}
                    />
                    <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-tighter">{preset.label}</span>
                  </button>
                ))}
              </div>

              {/* Background Upload */}
              <div className="flex gap-2">
                <input
                  ref={bgInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundUpload}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => bgInputRef.current?.click()}
                  className="flex-1 text-xs"
                >
                  Upload BG
                </Button>
                {backgroundImage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearBackground}
                    className="text-red-400 hover:bg-red-400/10 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {backgroundImage && (
                <div className="space-y-3 pt-2">
                  <div className="relative rounded-lg overflow-hidden border border-neutral-700">
                    <img
                      src={backgroundImage}
                      alt="Background preview"
                      className="w-full h-20 object-cover"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                      Zoom: {Math.round(backgroundImageScale * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="5"
                      step="0.02"
                      value={backgroundImageScale}
                      onChange={(e) => setBackgroundImageScale(parseFloat(e.target.value))}
                      className="w-full h-1.5 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                  </div>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleResetBackgroundPosition}
                    className="w-full text-[10px] h-7"
                  >
                    Reset Position
                  </Button>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Layer Management Section */}
          <CollapsibleSection title="Layer Management" defaultOpen={false}>
            <div className="space-y-4">
              {(nftElements.length > 0 || textElements.length > 0) ? (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {/* NFT Elements */}
                  {nftElements.map((element) => {
                    const hasBackground = element.nftData.layerAssets?.some(isBackgroundLayer);
                    return (
                      <div
                        key={element.id}
                        onClick={() => selectElement(element.id, 'nft')}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${
                          selectedElementId === element.id
                            ? 'bg-game-blue/10 border-game-blue shadow-sm'
                            : 'bg-neutral-800/30 border-transparent hover:border-neutral-700'
                        }`}
                      >
                        <img
                          src={element.nftData.imageUrl}
                          alt={element.nftData.name}
                          className="w-9 h-9 rounded object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-neutral-200 truncate">
                            {element.nftData.name}
                          </p>
                          <p className="text-[10px] text-neutral-500 font-medium uppercase tracking-tighter">NFT</p>
                        </div>
                        {hasBackground && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExclusiveNFTBackground(element.id, !element.useBackgroundLayer);
                            }}
                            className={`p-1.5 rounded-md transition-colors ${
                              element.useBackgroundLayer
                                ? 'bg-game-blue/20 text-game-blue hover:bg-game-blue/30'
                                : 'hover:bg-neutral-700 text-neutral-500 hover:text-neutral-400'
                            }`}
                            title={element.useBackgroundLayer ? 'Disable background' : 'Enable background'}
                          >
                            <BackgroundIcon className="w-3.5 h-3.5" active={element.useBackgroundLayer} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNFTElement(element.id);
                          }}
                          className="p-1.5 rounded-md hover:bg-red-400/10 text-neutral-500 hover:text-red-400 transition-colors"
                          title="Remove NFT"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}

                  {/* Text Elements */}
                  {textElements.map((element) => (
                    <div
                      key={element.id}
                      onClick={() => selectElement(element.id, 'text')}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${
                        selectedElementId === element.id
                          ? 'bg-game-blue/10 border-game-blue shadow-sm'
                          : 'bg-neutral-800/30 border-transparent hover:border-neutral-700'
                      }`}
                    >
                      <div className="w-9 h-9 rounded bg-neutral-700 flex items-center justify-center border border-neutral-600">
                        <span className="text-sm font-black text-neutral-400">T</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-neutral-200 truncate">
                          {element.text}
                        </p>
                        <p className="text-[10px] text-neutral-500 font-medium uppercase tracking-tighter">Text</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTextElement(element.id);
                        }}
                        className="p-1.5 rounded-md hover:bg-red-400/10 text-neutral-500 hover:text-red-400 transition-colors"
                        title="Remove text"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-neutral-600 text-center py-4 uppercase tracking-widest font-bold">
                  No layers yet
                </p>
              )}
            </div>
          </CollapsibleSection>

          {/* NFT Element Properties */}
          {selectedNFT && (
            <CollapsibleSection
              title={`NFT: ${selectedNFT.nftData.name.length > 18 ? selectedNFT.nftData.name.slice(0, 15) + '...' : selectedNFT.nftData.name}`}
              defaultOpen={true}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Scale: {Math.round(selectedNFT.scale * 50)}{selectedNFT.stickerMode && ' (locked)'}
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.02"
                    value={selectedNFT.scale}
                    onChange={(e) =>
                      updateNFTElementScale(selectedNFT.id, parseFloat(e.target.value))
                    }
                    disabled={selectedNFT.stickerMode}
                    className={`w-full h-1.5 bg-neutral-800 rounded-lg appearance-none accent-game-blue ${
                      selectedNFT.stickerMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Rotation: {selectedNFT.rotation}°
                  </label>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    step="5"
                    value={selectedNFT.rotation}
                    onChange={(e) =>
                      updateNFTElementRotation(selectedNFT.id, parseInt(e.target.value))
                    }
                    className="w-full h-1.5 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => bringToFront(selectedNFT.id, 'nft')}
                    className="flex-1 text-[10px]"
                  >
                    Bring Front
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => sendToBack(selectedNFT.id, 'nft')}
                    className="flex-1 text-[10px]"
                  >
                    Send Back
                  </Button>
                </div>

                {/* Sticker Mode Toggle */}
                {(hasBackgroundLayers || hasFrameLayers) && (
                  <div className="p-3 bg-gradient-to-r from-pink-500/10 to-game-blue/10 rounded-xl border border-pink-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-200">Sticker Mode</span>
                        <p className="text-[9px] text-neutral-500 mt-0.5">Remove background & frame</p>
                      </div>
                      <Toggle
                        checked={selectedNFT.stickerMode}
                        onChange={(checked) => updateNFTElementStickerMode(selectedNFT.id, checked)}
                      />
                    </div>
                  </div>
                )}

                {/* NFT Layer Toggles */}
                {hasFrameLayers && !selectedNFT.stickerMode && (
                  <div className="p-3 bg-neutral-800/50 rounded-xl space-y-4 border border-neutral-700">
                    {hasFrameLayers && (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-200">Frame</span>
                          <p className="text-[9px] text-neutral-500 mt-0.5">Toggle NFT frame</p>
                        </div>
                        <Toggle
                          checked={selectedNFT.useFrameLayer}
                          onChange={handleNFTFrameToggle}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => duplicateNFTElement(selectedNFT.id)}
                    className="flex-1 text-[10px]"
                  >
                    Duplicate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNFTElement(selectedNFT.id)}
                    className="flex-1 text-[10px] text-red-400 hover:bg-red-400/10"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Text Element Properties */}
          {selectedText && (
            <div ref={textPropertiesRef}>
              <CollapsibleSection title="Text Properties" defaultOpen={true}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Content</label>
                    {isNarrowViewport ? (
                      <button
                        type="button"
                        onClick={handleOpenMobileTextModal}
                        className="w-full px-3 py-2 text-left bg-neutral-800 border border-neutral-700 rounded-lg text-sm text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                      >
                        {selectedText.text || 'Tap to edit text'}
                      </button>
                    ) : (
                      <input
                        key={selectedText.id}
                        type="text"
                        dir="ltr"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        defaultValue={selectedText.text}
                        placeholder="YOUR TEXT HERE"
                        onFocus={(e) => enforceLtrInput(e.currentTarget)}
                        onInput={handleContentInput}
                        className="meme-text-input w-full px-3 py-2 text-left bg-neutral-800 border border-neutral-700 rounded-lg text-sm text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Font Family</label>
                    <FontDropdown
                      value={selectedText.fontFamily}
                      options={MEME_FONTS}
                      onChange={(font) => updateTextElement(selectedText.id, { fontFamily: font })}
                      buttonClassName="bg-neutral-800 border border-neutral-700 text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                      menuClassName="bg-neutral-800 border-neutral-700 text-neutral-200"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                      Font Size: {selectedText.fontSize}px
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="200"
                      step="2"
                      value={selectedText.fontSize}
                      onChange={(e) =>
                        updateTextElement(selectedText.id, { fontSize: parseInt(e.target.value) })
                      }
                      className="w-full h-1.5 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Fill</label>
                      <input
                        type="color"
                        value={selectedText.color}
                        onChange={(e) => updateTextElement(selectedText.id, { color: e.target.value })}
                        className="w-full h-8 rounded border border-neutral-700 cursor-pointer overflow-hidden"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Stroke</label>
                      <input
                        type="color"
                        value={selectedText.strokeColor}
                        onChange={(e) => updateTextElement(selectedText.id, { strokeColor: e.target.value })}
                        className="w-full h-8 rounded border border-neutral-700 cursor-pointer overflow-hidden"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                      Stroke Width: {selectedText.strokeWidth}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="1"
                      value={selectedText.strokeWidth}
                      onChange={(e) =>
                        updateTextElement(selectedText.id, { strokeWidth: parseInt(e.target.value) })
                      }
                      className="w-full h-1.5 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                      Rotation: {selectedText.rotation}°
                    </label>
                    <input
                      type="range"
                      min="-180"
                      max="180"
                      step="5"
                      value={selectedText.rotation}
                      onChange={(e) =>
                        updateTextElement(selectedText.id, { rotation: parseInt(e.target.value) })
                      }
                      className="w-full h-1.5 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => bringToFront(selectedText.id, 'text')}
                      className="flex-1 text-[10px]"
                    >
                      Bring Front
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => sendToBack(selectedText.id, 'text')}
                      className="flex-1 text-[10px]"
                    >
                      Send Back
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTextElement(selectedText.id)}
                    className="w-full text-[10px] text-red-400 hover:bg-red-400/10"
                  >
                    Delete Text
                  </Button>
                </div>
              </CollapsibleSection>
            </div>
          )}

          {/* Add some bottom padding */}
          <div className="h-4" />
        </div>
      </div>

      {/* NFT Search Modal */}
      <MemeNFTSearch />

      {isTextModalOpen && selectedText && (
        <div className="fixed inset-0 z-[120]">
          <button
            type="button"
            aria-label="Close text editor"
            className="absolute inset-0 bg-black/45"
            onClick={() => setIsTextModalOpen(false)}
          />
          <div
            className="absolute left-3 right-3 rounded-2xl border border-neutral-700 bg-neutral-900 shadow-2xl p-4"
            style={{ bottom: `${Math.max(12, keyboardInset + 12)}px` }}
          >
            <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
              Edit Text
            </label>
            <input
              type="text"
              dir="ltr"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={mobileTextDraft}
              onFocus={(e) => enforceLtrInput(e.currentTarget)}
              onChange={(e) => setMobileTextDraft(e.target.value)}
              className="meme-text-input w-full px-3 py-2 text-left bg-neutral-800 border border-neutral-700 rounded-lg text-sm text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
            />
            <div className="mt-3 flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsTextModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveMobileText}
                className="flex-1"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal for Telegram fallback */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url || ''}
        filename={previewImage?.filename || ''}
      />
    </div>
  );
}

export default EditorPage;
